/**
 * useSettings: the persisted preferences, kept deliberately
 * small. Four switches, one localStorage key, no server. Reading and writing are
 * synchronous and local; nothing here leaves the browser.
 *
 * Backed by `useLocalStorage` (→ `usePersistedState`), so every mounted
 * consumer of the same key stays in sync within the tab: flipping "clear on
 * close" in Settings immediately reaches the pagehide listener AppNext mounted
 * with an earlier copy.
 *
 * Text size also writes a cosmetic `--ui-scale` onto the document element so the
 * reading column can breathe at the chosen density. The scale is purely visual.
 * It changes no measurement, no byte count, no verdict.
 */
import { useCallback, useEffect } from "react";
import { useLocalStorage } from "../hooks/useLocalStorage";
import { STORAGE_KEYS } from "../utils/storageKeys";

export interface Settings {
  textSize: "compact" | "normal" | "comfortable";
  htmlDefault: "rendered" | "source";
  redactExports: boolean;
  clearOnClose: boolean;
  /** Where a freshly opened single message lands: the full report or Triage. */
  defaultDest: "inspect" | "triage";
}

const KEY = STORAGE_KEYS.settings;
/** Pre-namespace key, migrated once below. */
const LEGACY_KEY = "mi.settings";
const SETTINGS_VERSION = 1;

// One-shot legacy migration: an old bare mi.settings blob moves under the
// namespaced key, enveloped, and the stray is removed. Runs lazily on the
// first hook call (not at module load) so it sits after every const above
// it in initialization order.
let legacyMigrated = false;
function migrateLegacySettingsOnce(): void {
  // `typeof localStorage` is not a safe probe: it RESOLVES the Window accessor,
  // and Chrome throws SecurityError from that accessor when site data is
  // blocked. The guard therefore has to be `typeof window`, with the property
  // read itself inside the try. This runs during the first render of the
  // shell, so getting it wrong takes the app down before it paints, for
  // readers who chose the strictest privacy setting.
  if (legacyMigrated || typeof window === "undefined") return;
  legacyMigrated = true;
  try {
    const localStorage = window.localStorage;
    if (localStorage.getItem(KEY) == null) {
      const legacy = localStorage.getItem(LEGACY_KEY);
      if (legacy != null) {
        const parsed = JSON.parse(legacy) as Partial<Settings>;
        localStorage.setItem(
          KEY,
          JSON.stringify({ v: SETTINGS_VERSION, data: normalize(parsed) }),
        );
        localStorage.removeItem(LEGACY_KEY);
      }
    }
  } catch {
    /* unreadable legacy blob: defaults apply */
  }
}

const DEFAULTS: Settings = {
  textSize: "normal",
  htmlDefault: "rendered",
  redactExports: false,
  clearOnClose: false,
  defaultDest: "inspect",
};

const SCALE: Record<Settings["textSize"], string> = {
  compact: "0.94",
  normal: "1",
  comfortable: "1.06",
};

/** Validate a stored blob field-by-field, falling back to defaults. */
function normalize(parsed: Partial<Settings> | null | undefined): Settings {
  if (!parsed || typeof parsed !== "object") return DEFAULTS;
  return {
    textSize:
      parsed.textSize === "compact" ||
      parsed.textSize === "normal" ||
      parsed.textSize === "comfortable"
        ? parsed.textSize
        : DEFAULTS.textSize,
    htmlDefault:
      parsed.htmlDefault === "rendered" || parsed.htmlDefault === "source"
        ? parsed.htmlDefault
        : DEFAULTS.htmlDefault,
    redactExports:
      typeof parsed.redactExports === "boolean"
        ? parsed.redactExports
        : DEFAULTS.redactExports,
    clearOnClose:
      typeof parsed.clearOnClose === "boolean"
        ? parsed.clearOnClose
        : DEFAULTS.clearOnClose,
    defaultDest:
      parsed.defaultDest === "inspect" || parsed.defaultDest === "triage"
        ? parsed.defaultDest
        : DEFAULTS.defaultDest,
  };
}

/** Apply the cosmetic text scale to the document root. */
function applyScale(textSize: Settings["textSize"]): void {
  if (typeof document === "undefined") return;
  document.documentElement.style.setProperty("--ui-scale", SCALE[textSize]);
}

export function useSettings(): {
  settings: Settings;
  set: <K extends keyof Settings>(k: K, v: Settings[K]) => void;
  reset: () => void;
} {
  migrateLegacySettingsOnce();
  const [settings, setSettings] = useLocalStorage<Settings>(KEY, DEFAULTS, {
    // Envelope-aware: {v,data} is the current shape; a bare object is a v0
    // legacy write and normalizes as-is.
    parse: (raw) => {
      const parsed = JSON.parse(raw) as unknown;
      if (
        typeof parsed === "object" &&
        parsed !== null &&
        "v" in parsed &&
        "data" in parsed
      ) {
        return normalize((parsed as { data: Partial<Settings> }).data);
      }
      return normalize(parsed as Partial<Settings>);
    },
    stringify: (v) => JSON.stringify({ v: SETTINGS_VERSION, data: v }),
  });

  // Keep the document scale in step with the stored preference, including the
  // very first paint after a reload.
  useEffect(() => {
    applyScale(settings.textSize);
  }, [settings.textSize]);

  const set = useCallback(
    <K extends keyof Settings>(k: K, v: Settings[K]) => {
      setSettings((prev) => ({ ...prev, [k]: v }));
    },
    [setSettings],
  );

  const reset = useCallback(() => {
    setSettings({ ...DEFAULTS });
  }, [setSettings]);

  return { settings, set, reset };
}
